import { useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { alpha, Box, Button } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { tss } from 'tss-react/mui';
import { useParams } from 'react-router';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { useFragment, graphql } from 'react-relay';
import { H3 } from '../H3';
import { StyledPopper } from '../StyledPopper';
import { getPortfolioFullPath } from '../../RootPaths';
import { PricingModel } from '../../types/PricingModel';
import type { ResultFilter } from '../../schema.graphql';
import { UpDownArrowIcon } from '../UpDownArrowIcon.tsx';
import DownloadPQiFeedbackButton from './DownloadPQiFeedbackButton';
import DownloadPQiSummaryOrQualityReportButton from './DownloadPQiSummaryOrQualityReportButton';
import type { DownloadReportsPopupButton_surveys$key } from './__generated__/DownloadReportsPopupButton_surveys.graphql';
import type { DownloadReportsPopupButton_project$key } from './__generated__/DownloadReportsPopupButton_project.graphql';

const useStyles = tss.create(({ theme }) => ({
    table: {
        display: 'table',
        width: '100%',
        paddingBottom: 10,
    },
    row: {
        display: 'table-row',
        borderBottom: 0,
    },
    cellLeft: {
        display: 'table-cell',
        maxWidth: 70,
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
    },
    freeSummaryTitle: {
        display: 'table-cell',
        paddingLeft: 20,
        paddingRight: 20,
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
    },
    fullProjectTitle: {
        display: 'table-cell',
        paddingLeft: 10,
        paddingRight: 10,
        maxWidth: 130,
        textAlign: 'center',
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
    },
    feedbackTitle: {
        display: 'table-cell',
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
        paddingLeft: 20,
        paddingRight: 20,
        textAlign: 'center',
    },
    surveyNumberItem: {
        display: 'table-cell',
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
        maxWidth: 85,
        paddingTop: 10,
        paddingLeft: 10,
        verticalAlign: 'middle',
    },
    freeSummaryItem: {
        display: 'table-cell',
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
        padding: 20,
    },
    reportItem: {
        display: 'table-cell',
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
        padding: 25,
        textAlign: 'center',
    },
    fullProjectItem: {
        display: 'table-cell',
        borderBottom: `1px solid ${theme.bino.color.lightGray}`,
        maxWidth: 130,
        padding: 20,
        backgroundColor: alpha(theme.bino.color.primary, 0.05),
    },
    title: {
        fontSize: '20px',
        margin: 0,
        padding: 0,
    },
    content: {
        fontSize: '14px',
        marginTop: 10,
        textAlign: 'justify',
        color: theme.bino.color.darkGray,
    },
    projectTitle: {
        fontSize: 14,
        fontWeight: 'bold',
    },
    itemTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        margin: 0,
        paddingBottom: 10,
    },
    surveyInfo: {
        fontSize: 13,
        margin: 0,
        minWidth: 80,
        whiteSpace: 'nowrap',
        color: theme.bino.color.darkGray,
    },
    footer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    footerLink: {
        fontSize: 12,
        textAlign: 'right',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginTop: 5,
        paddingRight: 30,
        color: theme.bino.color.lightPurple,
        minWidth: 170,
    },
    footerContent: {
        marginTop: 5,
        fontSize: 12,
        color: theme.bino.color.darkGray,
        flexDirection: 'column',
        textAlign: 'right',
        '& > p': {
            margin: 0,
            padding: 0,
        },
    },
    downloadImageArrow: {
        display: 'inline-block',
        // position: ({ fullWidth }) => (fullWidth ? 'absolute' : 'static'),
        color: theme.bino.color.lightPurple,
        padding: `3px 0 0 ${theme.spacing(1)}`,
        // marginLeft: ({ fullWidth }) => (fullWidth ? `40%` : 'auto'),
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
}));

export type DownloadReportsPopupButtonProps = PropsWithChildren<{
    project: DownloadReportsPopupButton_project$key;
    surveys: DownloadReportsPopupButton_surveys$key;
    filter?: {
        description: ReactNode;
        filters: ReadonlyArray<ResultFilter>;
        filterMode: 'LogicalAnd' | 'LogicalOr';
    };
    // todo Need to pass in filters
    fullWidth?: boolean;
    buttonVariant?: ButtonProps['variant'];
    buttonText?: ReactNode; // Deprecated, use children
}>;

const PriceListHref = 'https://binosight.com/pricing';
const PriceListLink = (
    <a href={PriceListHref} target="_blank" rel="noopener noreferrer">
        {PriceListHref}
    </a>
);

export function DownloadReportsPopupButton({
    project: project$key,
    surveys: surveys$key,
    filter,
    fullWidth,
    buttonVariant,
    buttonText,
    children,
}: DownloadReportsPopupButtonProps) {
    const project = useFragment(projectFragment, project$key);
    const surveys = useFragment(surveysFragment, surveys$key);
    const { classes } = useStyles();
    const params = useParams();
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);
    const [anchorElement, setAnchorElement] = useState(null);

    // Temporary until all customers has switched to the subscription pricing model
    const canDownloadWithoutUnlocking =
        project.team.pricingModel !== PricingModel.UnlockResults;

    const filterProps = {
        filters: filter?.filters,
        filterMode: filter?.filterMode,
    };

    return (
        <>
            <Button
                fullWidth={fullWidth}
                variant={buttonVariant || 'outlined'}
                onClick={(event) => {
                    setAnchorElement(event.currentTarget);
                    setShowDownloadPopup(!showDownloadPopup);
                }}
                sx={{ minWidth: 30 }}
            >
                {children || buttonText || (
                    <FormattedMessage
                        defaultMessage="Download"
                        description="Download"
                    />
                )}
                <span className={classes.downloadImageArrow}>
                    <UpDownArrowIcon
                        direction={showDownloadPopup ? 'up' : 'down'}
                    />
                </span>
            </Button>
            <StyledPopper
                open={showDownloadPopup}
                onClose={() => setShowDownloadPopup(false)}
                anchorEl={anchorElement}
            >
                <Box maxWidth={640}>
                    <H3 className={classes.title}>
                        <FormattedMessage defaultMessage="Select report" />
                        {' - '}
                        {project.name}
                    </H3>
                    {filter?.description}
                    {canDownloadWithoutUnlocking ? (
                        <p className={classes.content}>
                            <FormattedMessage
                                defaultMessage="Download the quality report to get all results and insights. The feedback report is a short summary, suitable to be used as feedback to the project."
                                description="Information about the reports you can download"
                            />
                        </p>
                    ) : (
                        <>
                            <p className={classes.content}>
                                <FormattedMessage defaultMessage="Download the PQi Overview or purchase the complete PQi Quality Report to get all the results and insights." />
                            </p>
                            <p className={classes.content}>
                                <FormattedMessage
                                    defaultMessage="The price of the PQi Quality Report is based on the number of respondents. If your organization has an agreement regarding the purchase of PQi Quality Reports, the actual price may differ slightly from the price below which is based on Binosight's gross price list: {priceList}"
                                    values={{
                                        priceList: PriceListLink,
                                    }}
                                />
                            </p>
                        </>
                    )}
                    <div className={classes.table}>
                        <div className={classes.row}>
                            <div className={classes.cellLeft} />
                            {!canDownloadWithoutUnlocking && (
                                <div className={classes.freeSummaryTitle}>
                                    <p className={classes.projectTitle}>
                                        <FormattedMessage defaultMessage="PQi Summary" />
                                        *
                                    </p>
                                </div>
                            )}
                            <div className={classes.feedbackTitle}>
                                <p className={classes.projectTitle}>
                                    <FormattedMessage defaultMessage="PQi Feedback" />
                                    {!canDownloadWithoutUnlocking && '**'}
                                </p>
                            </div>
                            <div className={classes.fullProjectTitle}>
                                <p className={classes.projectTitle}>
                                    <FormattedMessage defaultMessage="PQi Quality Report" />
                                </p>
                            </div>
                        </div>
                        {surveys.map((survey, surveyIndex) => (
                            <div className={classes.row} key={surveyIndex}>
                                <div className={classes.surveyNumberItem}>
                                    <p className={classes.itemTitle}>
                                        <FormattedMessage
                                            defaultMessage="Survey #{surveyNumber}"
                                            values={{
                                                surveyNumber:
                                                    survey.surveyNumber,
                                            }}
                                        />
                                    </p>
                                    <p className={classes.surveyInfo}>
                                        <FormattedDate
                                            value={survey.surveyDate}
                                            year="numeric"
                                            month="short"
                                            day="2-digit"
                                            timeZone={survey.timeZone}
                                        />
                                    </p>
                                    <p className={classes.surveyInfo}>
                                        <FormattedMessage
                                            defaultMessage="{respondentCount, plural, one {1 respondent} other {# respondents}}"
                                            description="Number of responses"
                                            values={{
                                                respondentCount:
                                                    survey.dataSheet
                                                        ?.responseCount || 0,
                                            }}
                                        />
                                    </p>
                                    <p className={classes.surveyInfo}>
                                        <FormattedMessage
                                            defaultMessage="{commentCount, plural, one {1 comment} other {# comments}}"
                                            description="Number of comments"
                                            values={{
                                                commentCount:
                                                    survey.dataSheet
                                                        ?.commentCount || 0,
                                            }}
                                        />
                                    </p>
                                </div>
                                {!canDownloadWithoutUnlocking && (
                                    <div className={classes.freeSummaryItem}>
                                        <DownloadPQiSummaryOrQualityReportButton
                                            freeSummary
                                            survey={survey}
                                            {...filterProps}
                                        />
                                    </div>
                                )}
                                <div className={classes.reportItem}>
                                    <DownloadPQiFeedbackButton
                                        survey={survey}
                                        {...filterProps}
                                    />
                                </div>

                                <div className={classes.fullProjectItem}>
                                    <DownloadPQiSummaryOrQualityReportButton
                                        survey={survey}
                                        respondentCount={
                                            survey.dataSheet?.responseCount || 0
                                        }
                                        {...filterProps}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={classes.footer}>
                        <a
                            href={`/${getPortfolioFullPath(
                                params,
                            )}/help-center`}
                            className={classes.footerLink}
                            style={{
                                visibility:
                                    'hidden' /* FIXME Put back when the help center exists */,
                            }}
                        >
                            <FormattedMessage defaultMessage="About quality reports" />
                        </a>
                        {!canDownloadWithoutUnlocking && (
                            <div className={classes.footerContent}>
                                <p>
                                    {'* '}
                                    <FormattedMessage defaultMessage="Possible to download free as soon as then survey closed" />
                                </p>
                                <p>
                                    {'** '}
                                    <FormattedMessage defaultMessage="Possible to download free when you have purchased the Quality Reports" />
                                </p>
                            </div>
                        )}
                    </div>
                </Box>
            </StyledPopper>
        </>
    );
}

const projectFragment = graphql`
    fragment DownloadReportsPopupButton_project on ProjectInterface {
        name
        team {
            pricingModel
        }
    }
`;
const surveysFragment = graphql`
    fragment DownloadReportsPopupButton_surveys on ProjectSurvey
    @relay(plural: true) {
        surveyDate
        timeZone
        resultsUnlocked
        surveyNumber
        dataSheet {
            respondentCount
            responseRate
            responseCount
            commentCount
        }
        ...DownloadPQiSummaryOrQualityReportButton_survey
        ...DownloadPQiFeedbackButton_survey
    }
`;
